import { Button } from "@/components/ui/button";
import {
  CircleDollarSign, Receipt, Users, Clock, ShieldAlert, ListChecks,
} from "lucide-react";
import { trackEstimate } from "@/lib/track";

const PAINS = [
  { n: "01", icon: CircleDollarSign, title: "Unclear Pricing", text: "Different contractors give different quotations, making it difficult to understand the real renovation budget." },
  { n: "02", icon: Receipt, title: "Unexpected Costs", text: "Changes in scope and unclear inclusions can make renovation costs difficult to control." },
  { n: "03", icon: Users, title: "Too Many Contractors", text: "Coordinating demolition, electrical, plumbing, flooring, ceilings, painting and joinery separately can become exhausting." },
  { n: "04", icon: Clock, title: "Renovation Delays", text: "Delays can affect your move-in plans, rental plans and daily life." },
  { n: "05", icon: ShieldAlert, title: "Quality Concerns", text: "Poor finishing, inconsistent workmanship or unsuitable materials can become expensive to correct." },
  { n: "06", icon: ListChecks, title: "Renovation Stress", text: "You shouldn't have to spend your time coordinating workers, suppliers and different subcontractors." },
];

export default function PainSection() {
  return (
    <section id="pain" data-testid="pain-section" className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold mb-3">
            We Understand
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-neutral-900 mb-4 leading-[1.1]">
            Renovating Your Property Shouldn&apos;t Become a Headache.
          </h2>
          <p className="text-base lg:text-lg text-neutral-600 leading-relaxed">
            Before renovation starts, property owners often worry about unclear pricing, unexpected costs, delays, poor finishing and having to manage multiple contractors.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6 mb-12 lg:mb-16">
          {PAINS.map((p) => (
            <div
              key={p.n}
              data-testid={`pain-card-${p.n}`}
              className="bg-[#F9F8F6] border border-neutral-200 p-7 hover:border-[#C5A059] hover:shadow-[0_20px_45px_rgba(0,0,0,0.06)] transition-all duration-300 group"
            >
              <div className="flex items-start justify-between mb-5">
                <div className="w-12 h-12 bg-[#C5A059]/10 flex items-center justify-center group-hover:bg-[#C5A059] transition-colors duration-300">
                  <p.icon className="w-6 h-6 text-[#C5A059] group-hover:text-white transition-colors duration-300" strokeWidth={1.75} />
                </div>
                <span className="font-heading text-3xl font-light text-[#C5A059]/60 group-hover:text-[#C5A059] transition-colors duration-300">
                  {p.n}
                </span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-neutral-900 mb-2.5 uppercase tracking-wide">
                {p.title}
              </h3>
              <p className="text-[15px] text-neutral-700 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>

        <div className="bg-neutral-950 text-white px-8 py-12 lg:px-16 lg:py-14 text-center relative overflow-hidden" data-testid="pain-solution">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[380px] h-[380px] rounded-full bg-[#C5A059]/10 blur-[100px] pointer-events-none" />
          <div className="relative">
            <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-light tracking-tight mb-4 leading-[1.15]">
              One Professional Team. <span className="text-[#C5A059]">One Clear Renovation Process.</span>
            </h3>
            <p className="text-neutral-300 text-base lg:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
              Fix Technical Renovation Company manages the renovation from demolition to final finishing under one professional team, giving you one point of contact for the complete project.
            </p>
            <a href="#lead-form" data-testid="pain-cta" onClick={() => trackEstimate("pain_section")}>
              <Button className="bg-[#C5A059] hover:bg-[#A88746] text-white rounded-none px-8 py-7 text-base font-semibold tracking-wide">
                GET MY ROUGH ESTIMATE
              </Button>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
